import React from 'react';
import { Box, Typography, Button, Stack } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import { GoogleCountrySelectionOption } from '../../types';

interface CompanySelectorProps {
  options: GoogleCountrySelectionOption[];
  onSelect: (country: string) => void;
  disabled?: boolean;
}

/**
 * Phase 1B (CLAUDE.md §1/§2): pre-auth, one-shot "which company are you
 * working as" pick screen for the rare (~1-2 people) dual-account case -
 * Google auth succeeded and the same email exists in both AR's and UY's
 * databases. Shown by Login.tsx in place of navigating straight through;
 * picking one re-posts the same Google id_token plus the choice to
 * `/auth/google/select-country` and proceeds like a normal login.
 *
 * Deliberately not a repurposing of the old in-session country switcher
 * (retired - CLAUDE.md §1): that was a live in-app toggle for an
 * already-authenticated user; this is a one-time step *before* a session
 * exists at all, and switching again later means logging out and back in
 * through this same screen, not an in-app control.
 */
export const CompanySelector: React.FC<CompanySelectorProps> = ({ options, onSelect, disabled }) => {
  return (
    <Box sx={{ width: '100%', textAlign: 'center' }}>
      <Typography variant="h6" gutterBottom>
        ¿Con qué empresa querés ingresar?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Tu cuenta de Google está asociada a más de un país.
      </Typography>
      <Stack spacing={1.5}>
        {options.map((option) => (
          <Button
            key={option.country}
            variant="outlined"
            fullWidth
            size="large"
            startIcon={<BusinessIcon />}
            disabled={disabled}
            onClick={() => onSelect(option.country)}
            sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
          >
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="body1">{option.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {option.country === 'AR' ? 'Argentina' : option.country === 'UY' ? 'Uruguay' : option.country}
              </Typography>
            </Box>
          </Button>
        ))}
      </Stack>
    </Box>
  );
};
